import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";
import styles from "./InterventionWidget.module.scss";
import { LinkType, Links } from "../Links/Links";
import { Spinner } from "../../../shared/components/Spinner/Spinner";
import { thousandize } from "../../../../util/formatting";
import { PortableText } from "@portabletext/react";
import { NavLink } from "../../../shared/components/Navbar/Navbar";
import { stegaClean } from "@sanity/client/stega";
import { EffektButton } from "../../../shared/components/EffektButton/EffektButton";
import { WidgetContext } from "../../layout/layout";
import { usePlausible } from "next-plausible";
import { fetchInterventionCosts } from "./impactCosts";
export type InterventionWidgetOutputConfiguration = {
  interventions: SanityIntervention[];
  explanation_label: string;
  explanation_text: any[];
  explanation_links?: (LinkType | NavLink)[];
  donate_button?: boolean;
  donate_label_short?: string;
  locale: string;
};
export type SanityIntervention = {
  title: string;
  organization_name: string;
  abbreviation: string;
  template_string: string;
  organization_id: number;
  cause_area_id: number;
};
type Intervention = {
  title: string;
  organizationName: string;
  abbreviation: string;
  pricePerOutput?: number;
  outputStringTemplate: string;
  organizationId: number;
  causeAreaId: number;
  shortDescription: string;
};
export const InterventionWidgetOutput: React.FC<{
  sum: number;
  configuration: InterventionWidgetOutputConfiguration;
  currency: string;
  locale: string;
}> = t0 => {
  const $ = _c(50);
  const {
    sum,
    configuration,
    currency
  } = t0;
  const {
    interventions,
    explanation_label,
    explanation_text,
    explanation_links
  } = configuration;
  const [contextExpanded, setContextExpanded] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [interventionCosts, setInterventionCosts] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [interventionShortDescriptions, setInterventionShortDescriptions] = useState(t2);
  const [selectedIntervention, setSelectedIntervention] = useState(interventions ? interventions[0].title : "");
  const [, setWidgetContext] = useContext(WidgetContext);
  const plausible = usePlausible();
  let t3;
  let t4;
  if ($[2] !== configuration.locale || $[3] !== currency || $[4] !== interventions) {
    t3 = () => {
      if (!interventions || interventions.length === 0) {
        return;
      }
      let cancelled = false;
      fetchInterventionCosts({
        abbreviations: interventions.map(_temp),
        locale: stegaClean(configuration.locale),
        currency: stegaClean(currency)
      }).then(byAbbreviation => {
        if (cancelled) {
          return;
        }
        const costs = new Map();
        const shortDescriptions = new Map();
        for (const i_0 of interventions) {
          const abbreviation = stegaClean(i_0.abbreviation);
          const cost = byAbbreviation.get(abbreviation.toUpperCase());
          if (!cost) {
            continue;
          }
          costs.set(abbreviation, cost.costPerOutput);
          shortDescriptions.set(abbreviation, cost.shortDescription);
        }
        setInterventionCosts(costs);
        setInterventionShortDescriptions(shortDescriptions);
      }).catch(_temp2);
      return () => {
        cancelled = true;
      };
    };
    t4 = [interventions, currency, configuration.locale];
    $[2] = configuration.locale;
    $[3] = currency;
    $[4] = interventions;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  if (typeof interventions === "undefined" || interventions.length === 0) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span>No internventions</span>;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== configuration.donate_button || $[9] !== configuration.donate_label_short || $[10] !== contextExpanded || $[11] !== explanation_label || $[12] !== explanation_links || $[13] !== explanation_text || $[14] !== interventionCosts || $[15] !== interventionShortDescriptions || $[16] !== interventions || $[17] !== plausible || $[18] !== selectedIntervention || $[19] !== setWidgetContext || $[20] !== sum) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t11;
      if ($[27] !== interventionCosts || $[28] !== interventionShortDescriptions) {
        t11 = i_1 => ({
          title: i_1.title,
          abbreviation: i_1.abbreviation,
          pricePerOutput: interventionCosts.get(stegaClean(i_1.abbreviation)),
          outputStringTemplate: i_1.template_string,
          organizationName: i_1.organization_name,
          organizationId: i_1.organization_id,
          causeAreaId: i_1.cause_area_id,
          shortDescription: interventionShortDescriptions.get(stegaClean(i_1.abbreviation)) || ""
        });
        $[27] = interventionCosts;
        $[28] = interventionShortDescriptions;
        $[29] = t11;
      } else {
        t11 = $[29];
      }
      const mappedInterventions = interventions.map(t11);
      let t12;
      if ($[30] !== selectedIntervention) {
        t12 = i_2 => stegaClean(i_2.title) === selectedIntervention;
        $[30] = selectedIntervention;
        $[31] = t12;
      } else {
        t12 = $[31];
      }
      const currentIntervention = mappedInterventions.find(t12) as Intervention;
      if (!currentIntervention) {
        setSelectedIntervention(stegaClean(mappedInterventions[0].title));
        let t13;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t13 = <Spinner />;
          $[32] = t13;
        } else {
          t13 = $[32];
        }
        t10 = t13;
        break bb0;
      }
      const output = currentIntervention.pricePerOutput ? Math.round(sum / currentIntervention.pricePerOutput) : 0;
      const loading = typeof currentIntervention.pricePerOutput === "undefined";
      let outputString;
      if (isNaN(output)) {
        outputString = "0";
      } else {
        outputString = output.toString();
      }
      t8 = styles.wrapper;
      let t13;
      if ($[33] !== interventions || $[34] !== selectedIntervention) {
        let t14;
        if ($[36] !== selectedIntervention) {
          t14 = i_3 => <button data-cy={`${i_3.title}-button`} key={i_3.title} onClick={e => {
            e.currentTarget.blur();
            setSelectedIntervention(stegaClean(i_3.title));
          }} className={stegaClean(i_3.title) === selectedIntervention ? styles.selected : ""}>{i_3.title}</button>;
          $[36] = selectedIntervention;
          $[37] = t14;
        } else {
          t14 = $[37];
        }
        t13 = interventions.map(t14);
        $[33] = interventions;
        $[34] = selectedIntervention;
        $[35] = t13;
      } else {
        t13 = $[35];
      }
      if ($[38] !== t13) {
        t9 = <div className={styles.select}>{t13}</div>;
        $[38] = t13;
        $[39] = t9;
      } else {
        t9 = $[39];
      }
      t5 = styles.output;
      if ($[40] !== loading) {
        t6 = loading && <div className={styles.spinnerWrapper}><Spinner /></div>;
        $[40] = loading;
        $[41] = t6;
      } else {
        t6 = $[41];
      }
      t7 = !loading && <><span className="detailheader">{currentIntervention.organizationName}</span><div className={styles.paragraphWrapper}><span data-cy="impact-output" className={styles.paragraphNumber}>{thousandize(parseInt(outputString))}</span><div className={styles.explanatory}><p><span className={styles.innerParagraphNumber}>{outputString}</span>{currentIntervention.outputStringTemplate}</p><span className={contextExpanded ? styles.captionopen : ""} onClick={() => setContextExpanded(!contextExpanded)}>{explanation_label}  </span></div></div><AnimateHeight duration={300} height={contextExpanded ? "auto" : 0} animateOpacity={true}><div className={styles.context}><PortableText value={explanation_text} /></div>{explanation_links && <Links links={explanation_links} />}</AnimateHeight>{configuration.donate_button && <div className={styles.giveButtonWrapper}><EffektButton onClick={() => {
            plausible("OpenDonationWidget", {
              props: {
                page: window.location.pathname
              }
            });
            plausible("OpenDonationWidgetInterventionWidgetCTA", {
              props: {
                page: window.location.pathname
              }
            });
            setWidgetContext({
              open: true,
              prefilled: [{
                causeAreaId: currentIntervention.causeAreaId,
                share: 100,
                organizations: [{
                  organizationId: currentIntervention.organizationId,
                  share: 100
                }]
              }],
              prefilledSum: sum,
              prefilledRecurring: null
            });
          }}>{configuration.donate_label_short?.replace(".", "") || "Gi"}{" "}{thousandize(parseInt(outputString))}{" "}{currentIntervention.shortDescription.toLowerCase()}</EffektButton></div>}</>;
    }
    $[8] = configuration.donate_button;
    $[9] = configuration.donate_label_short;
    $[10] = contextExpanded;
    $[11] = explanation_label;
    $[12] = explanation_links;
    $[13] = explanation_text;
    $[14] = interventionCosts;
    $[15] = interventionShortDescriptions;
    $[16] = interventions;
    $[17] = plausible;
    $[18] = selectedIntervention;
    $[19] = setWidgetContext;
    $[20] = sum;
    $[21] = t10;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t10 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[42] !== t5 || $[43] !== t6 || $[44] !== t7) {
    t11 = <div className={t5}>{t6}{t7}</div>;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t11 || $[47] !== t8 || $[48] !== t9) {
    t12 = <div className={t8}>{t9}{t11}</div>;
    $[46] = t11;
    $[47] = t8;
    $[48] = t9;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  return t12;
};
function _temp(i) {
  return stegaClean(i.abbreviation);
}
function _temp2(err) {
  console.error(err);
}